import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row, Col, ListGroup, Card, Form, InputGroup, Button, Badge,
  Spinner, Alert, Modal,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { API_URL, getDeviceId } from '../api';

// --- Types matching backend response ---

interface ListItemResponse {
  id: string;
  list_id: string;
  product_id: number | null;
  concept_id: number | null;
  custom_name: string | null;
  quantity: string | null;
  note: string | null;
  is_checked: boolean;
  sort_order: number;
}

interface ShoppingListSummary {
  id: string;
  name: string;
  device_id: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  item_count: number;
  unchecked_count: number;
}

interface ShoppingListDetail {
  id: string;
  name: string;
  device_id: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  items: ListItemResponse[];
}

interface AutocompleteSuggestion {
  type: 'concept' | 'product';
  id: number;
  name: string;
  icon: string | null;
  default_unit: string | null;
  default_quantity: number | null;
  brand: string | null;
}

// --- Constants ---

const ACTIVE_LIST_KEY = 'smartcart_active_list';
const UNIT_LABELS: Record<string, string> = {
  stueck: 'Stk', kg: 'kg', g: 'g', l: 'l', ml: 'ml',
  packung: 'Pkg', bund: 'Bund', dose: 'Dose', flasche: 'Fl',
};

// --- Component ---

const ShoppingLists: React.FC = () => {
  const [lists, setLists] = useState<ShoppingListSummary[]>([]);
  const [activeListId, setActiveListId] = useState<string | null>(
    localStorage.getItem(ACTIVE_LIST_KEY)
  );
  const [activeList, setActiveList] = useState<ShoppingListDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New item form
  const [newItemName, setNewItemName] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState('');
  const [newItemNote, setNewItemNote] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState<AutocompleteSuggestion | null>(null);

  // Autocomplete
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // New list modal
  const [showNewListModal, setShowNewListModal] = useState(false);
  const [newListName, setNewListName] = useState('');

  // Rename modal
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const deviceId = getDeviceId();
  const navigate = useNavigate();

  // --- API Calls ---

  const fetchLists = useCallback(async () => {
    try {
      const params = new URLSearchParams({ device_id: deviceId });
      const res = await fetch(`${API_URL}/shopping_lists/?${params}`);
      if (!res.ok) throw new Error('Fehler beim Laden der Listen');
      const data: ShoppingListSummary[] = await res.json();
      setLists(data);
      return data;
    } catch (e: any) {
      setError(e.message);
      return [];
    }
  }, [deviceId]);

  const fetchListDetail = useCallback(async (listId: string) => {
    setListLoading(true);
    try {
      const res = await fetch(`${API_URL}/shopping_lists/${listId}`);
      if (!res.ok) throw new Error('Liste nicht gefunden');
      const data: ShoppingListDetail = await res.json();
      setActiveList(data);
    } catch (e: any) {
      setError(e.message);
      setActiveList(null);
    } finally {
      setListLoading(false);
    }
  }, []);

  const fetchSuggestions = async (query: string) => {
    if (query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const params = new URLSearchParams({ q: query.trim(), limit: '8' });
      const res = await fetch(`${API_URL}/shopping_lists/autocomplete?${params}`);
      if (!res.ok) return;
      const data: AutocompleteSuggestion[] = await res.json();
      setSuggestions(data);
      setShowSuggestions(data.length > 0);
      setSelectedIndex(-1);
    } catch {
      // silently ignore autocomplete errors
    }
  };

  const createList = async (name: string) => {
    try {
      const res = await fetch(`${API_URL}/shopping_lists/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, device_id: deviceId }),
      });
      if (!res.ok) throw new Error('Fehler beim Erstellen der Liste');
      const created: ShoppingListDetail = await res.json();
      await fetchLists();
      selectList(created.id);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const renameList = async () => {
    if (!activeListId || !renameValue.trim()) return;
    try {
      const res = await fetch(`${API_URL}/shopping_lists/${activeListId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: renameValue.trim() }),
      });
      if (!res.ok) throw new Error('Fehler beim Umbenennen');
      setShowRenameModal(false);
      await fetchLists();
      await fetchListDetail(activeListId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const deleteList = async (listId: string) => {
    try {
      const res = await fetch(`${API_URL}/shopping_lists/${listId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Fehler beim Löschen');
      const remaining = await fetchLists();
      if (activeListId === listId) {
        if (remaining.length > 0) {
          selectList(remaining[0].id);
        } else {
          setActiveListId(null);
          setActiveList(null);
          localStorage.removeItem(ACTIVE_LIST_KEY);
        }
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  const addItem = async (suggestion?: AutocompleteSuggestion | null) => {
    if (!activeListId) return;
    const picked = suggestion ?? selectedSuggestion;
    const nameText = newItemName.trim();
    if (!picked && !nameText) return;

    const body: Record<string, any> = {
      quantity: newItemQuantity.trim() || null,
      note: newItemNote.trim() || null,
    };

    if (picked?.type === 'concept') {
      body.concept_id = picked.id;
      body.custom_name = picked.name;
      body.specificity = 'vague';
    } else if (picked?.type === 'product') {
      body.product_id = picked.id;
      body.custom_name = picked.name;
      body.specificity = 'specific';
    } else {
      body.custom_name = nameText;
    }

    try {
      const res = await fetch(`${API_URL}/shopping_lists/${activeListId}/items/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Fehler beim Hinzufügen');
      setNewItemName('');
      setNewItemQuantity('');
      setNewItemNote('');
      setSelectedSuggestion(null);
      setSuggestions([]);
      setShowSuggestions(false);
      await fetchListDetail(activeListId);
      await fetchLists();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const toggleItem = async (item: ListItemResponse) => {
    try {
      const res = await fetch(`${API_URL}/shopping_lists/items/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_checked: !item.is_checked }),
      });
      if (!res.ok) throw new Error('Fehler beim Aktualisieren');
      await fetchListDetail(item.list_id);
      await fetchLists();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const deleteItem = async (item: ListItemResponse) => {
    try {
      const res = await fetch(`${API_URL}/shopping_lists/items/${item.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Fehler beim Löschen');
      await fetchListDetail(item.list_id);
      await fetchLists();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // --- Helpers ---

  const selectList = (listId: string) => {
    setActiveListId(listId);
    localStorage.setItem(ACTIVE_LIST_KEY, listId);
    fetchListDetail(listId);
  };

  const selectSuggestion = (s: AutocompleteSuggestion) => {
    setSelectedSuggestion(s);
    setNewItemName(s.icon ? `${s.icon} ${s.name}` : s.name);
    setShowSuggestions(false);
    setSuggestions([]);
    // Pre-fill quantity from concept defaults
    if (s.type === 'concept' && s.default_quantity && s.default_unit) {
      const unitLabel = UNIT_LABELS[s.default_unit] || s.default_unit;
      setNewItemQuantity(`${s.default_quantity} ${unitLabel}`);
    }
  };

  const handleNameChange = (value: string) => {
    setNewItemName(value);
    setSelectedSuggestion(null); // clear selection when user types again
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') addItem();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0) {
        selectSuggestion(suggestions[selectedIndex]);
      } else {
        setShowSuggestions(false);
        addItem();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const sortedItems = (items: ListItemResponse[]): ListItemResponse[] => {
    const unchecked = items.filter(i => !i.is_checked).sort((a, b) => a.sort_order - b.sort_order);
    const checked = items.filter(i => i.is_checked).sort((a, b) => a.sort_order - b.sort_order);
    return [...unchecked, ...checked];
  };

  const itemDisplayName = (item: ListItemResponse): string => {
    return item.custom_name || `Produkt #${item.product_id}`;
  };

  // --- Effects ---

  useEffect(() => {
    (async () => {
      setLoading(true);
      const data = await fetchLists();
      if (data.length > 0) {
        const savedId = localStorage.getItem(ACTIVE_LIST_KEY);
        const validSaved = savedId && data.some(l => l.id === savedId);
        const targetId = validSaved ? savedId! : data[0].id;
        setActiveListId(targetId);
        localStorage.setItem(ACTIVE_LIST_KEY, targetId);
        await fetchListDetail(targetId);
      }
      setLoading(false);
    })();
  }, [fetchLists, fetchListDetail]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Render ---

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" /> <span className="ms-2">Laden...</span>
      </div>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Row>
        {/* Left panel: List of lists */}
        <Col md={4} lg={3} className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Einkaufslisten</h5>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => { setNewListName(''); setShowNewListModal(true); }}
            >
              + Neu
            </Button>
          </div>

          {lists.length === 0 ? (
            <Alert variant="info">
              Noch keine Listen vorhanden. Erstelle deine erste Einkaufsliste!
            </Alert>
          ) : (
            <ListGroup>
              {lists.map(list => (
                <ListGroup.Item
                  key={list.id}
                  active={list.id === activeListId}
                  action
                  onClick={() => selectList(list.id)}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div className="text-truncate me-2">{list.name}</div>
                  <Badge bg={list.id === activeListId ? 'light' : 'secondary'} text={list.id === activeListId ? 'dark' : undefined}>
                    {list.unchecked_count}/{list.item_count}
                  </Badge>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Col>

        {/* Right panel: Active list detail */}
        <Col md={8} lg={9}>
          {!activeList && !listLoading ? (
            <div className="text-center text-muted mt-5">
              <p>Wähle eine Liste aus oder erstelle eine neue.</p>
            </div>
          ) : listLoading ? (
            <div className="text-center mt-5">
              <Spinner animation="border" size="sm" /> <span className="ms-2">Laden...</span>
            </div>
          ) : activeList && (
            <>
              {/* List header */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">{activeList.name}</h4>
                <div>
                  <Button
                    size="sm"
                    variant="success"
                    className="me-2"
                    onClick={() => navigate(`/shopping-lists/${activeList.id}/compare`)}
                    disabled={activeList.items.length === 0}
                  >
                    Preisvergleich
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="me-2"
                    onClick={() => {
                      setRenameValue(activeList.name);
                      setShowRenameModal(true);
                    }}
                  >
                    Umbenennen
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => {
                      if (window.confirm(`"${activeList.name}" wirklich löschen?`)) {
                        deleteList(activeList.id);
                      }
                    }}
                  >
                    Löschen
                  </Button>
                </div>
              </div>

              {/* Add item form */}
              <Card className="mb-3">
                <Card.Body>
                  <InputGroup className="mb-2">
                    <div ref={wrapperRef} style={{ position: 'relative', flex: 1 }}>
                      <Form.Control
                        placeholder="Produkt hinzufügen..."
                        value={newItemName}
                        onChange={e => handleNameChange(e.target.value)}
                        onKeyDown={handleNameKeyDown}
                        onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                        autoComplete="off"
                      />
                      {showSuggestions && suggestions.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1050,
                          background: 'white',
                          border: '1px solid #dee2e6',
                          borderTop: 'none',
                          borderRadius: '0 0 0.375rem 0.375rem',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                          maxHeight: '300px',
                          overflowY: 'auto',
                        }}>
                          {suggestions.map((s, idx) => (
                            <div
                              key={`${s.type}-${s.id}`}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                backgroundColor: idx === selectedIndex ? '#e9ecef' : 'white',
                                borderBottom: '1px solid #f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                              onMouseEnter={() => setSelectedIndex(idx)}
                              onMouseDown={e => {
                                e.preventDefault(); // keep focus on input
                                selectSuggestion(s);
                              }}
                            >
                              {s.type === 'concept' ? (
                                <>
                                  <span style={{ fontSize: '1.2em', width: '24px', textAlign: 'center' }}>
                                    {s.icon || ''}
                                  </span>
                                  <span style={{ fontWeight: 500 }}>{s.name}</span>
                                  <Badge bg="info" className="ms-auto" style={{ fontSize: '0.7em' }}>
                                    Konzept
                                  </Badge>
                                </>
                              ) : (
                                <>
                                  <span style={{ width: '24px', textAlign: 'center', color: '#6c757d' }}>
                                    &#9679;
                                  </span>
                                  <span>{s.name}</span>
                                  {s.brand && (
                                    <small className="text-muted ms-auto">{s.brand}</small>
                                  )}
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Form.Control
                      placeholder="Menge"
                      value={newItemQuantity}
                      onChange={e => setNewItemQuantity(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addItem(); }}
                      style={{ maxWidth: '120px' }}
                    />
                    <Button variant="primary" onClick={() => addItem()} disabled={!newItemName.trim()}>
                      +
                    </Button>
                  </InputGroup>
                  <Form.Control
                    size="sm"
                    placeholder="Notiz (optional)"
                    value={newItemNote}
                    onChange={e => setNewItemNote(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addItem(); }}
                  />
                </Card.Body>
              </Card>

              {/* Items */}
              {activeList.items.length === 0 ? (
                <Alert variant="light" className="text-center text-muted">
                  Liste ist leer. Füge oben dein erstes Produkt hinzu.
                </Alert>
              ) : (
                <ListGroup>
                  {sortedItems(activeList.items).map(item => (
                    <ListGroup.Item
                      key={item.id}
                      className="d-flex align-items-center"
                      style={item.is_checked ? { opacity: 0.5 } : undefined}
                    >
                      <Form.Check
                        type="checkbox"
                        checked={item.is_checked}
                        onChange={() => toggleItem(item)}
                        className="me-3"
                      />
                      <div className="flex-grow-1">
                        <span style={item.is_checked ? { textDecoration: 'line-through' } : undefined}>
                          {itemDisplayName(item)}
                        </span>
                        {item.quantity && (
                          <Badge bg="light" text="dark" className="ms-2">{item.quantity}</Badge>
                        )}
                        {item.note && (
                          <small className="text-muted ms-2">— {item.note}</small>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => deleteItem(item)}
                      >
                        &times;
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </>
          )}
        </Col>
      </Row>

      {/* New List Modal */}
      <Modal show={showNewListModal} onHide={() => setShowNewListModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Neue Einkaufsliste</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            placeholder="Name der Liste"
            value={newListName}
            onChange={e => setNewListName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && newListName.trim()) {
                createList(newListName.trim());
                setShowNewListModal(false);
              }
            }}
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewListModal(false)}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            disabled={!newListName.trim()}
            onClick={() => {
              createList(newListName.trim());
              setShowNewListModal(false);
            }}
          >
            Erstellen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Rename Modal */}
      <Modal show={showRenameModal} onHide={() => setShowRenameModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Liste umbenennen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && renameValue.trim()) {
                renameList();
              }
            }}
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRenameModal(false)}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            disabled={!renameValue.trim()}
            onClick={renameList}
          >
            Speichern
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ShoppingLists;
